// ---------------------------------------------------------------------------
// The guide registry. One flat map of every guide the app can run.
//
// Guide ids are a public contract: they are stored in each user's progress rows
// and they are what the Dex coach offers by name. Once a guide has shipped, its
// id is frozen. Renaming one orphans progress and breaks every offer Dex has
// already learned to make.
//
// Add a new domain by dropping a module in this folder and listing it below.
// ---------------------------------------------------------------------------

import orientationGuides from './orientation';
import setupGuides from './setup';
import membersGuides from './members';
import billingGuides from './billing';
import frontdeskGuides from './frontdesk';
import financeGuides from './finance';
import salesGuides from './sales';
import operationsGuides from './operations';
import communityGuides from './community';

const MODULES = [
  orientationGuides,
  setupGuides,
  membersGuides,
  billingGuides,
  frontdeskGuides,
  financeGuides,
  salesGuides,
  operationsGuides,
  communityGuides,
];

const ALL = MODULES.flat();

export const GUIDES = Object.freeze(
  ALL.reduce((acc, guide) => {
    if (acc[guide.id]) {
      throw new Error(`[coach] duplicate guide id "${guide.id}"`);
    }
    acc[guide.id] = guide;
    return acc;
  }, {})
);

export const GUIDE_LIST = Object.freeze(ALL);

export function getGuide(id) {
  return GUIDES[id] || null;
}

export function guideExists(id) {
  return !!GUIDES[id];
}

/**
 * Does this user hold what a guide's `permission` asks for?
 *
 * A guide may name one code or an array of them, and an array means all of
 * them. That is not pedantry: a guide almost always needs the view code that
 * opens the page AND the manage code for the button it tells you to press, and
 * gating on either one alone dead-ends somebody on a 403 or on a control that
 * is not there. journeys.py `_allowed` applies exactly the same rule server
 * side, and the two must not drift.
 */
function meetsPermission(need, hasPermission) {
  if (!need || !hasPermission) return true;
  const codes = Array.isArray(need) ? need : [need];
  return codes.every((code) => hasPermission(code));
}

/**
 * May this user run this guide at all?
 *
 * The single answer to that question. Listing a guide, offering it in a Dex
 * answer and starting it from a pasted link are three different code paths, and
 * a link is the one that arrives from outside: anybody can hand anybody a
 * /admin/guides/<id> URL. So this is enforced where a guide STARTS, not only
 * where one is listed, and every caller asks the same function.
 *
 * @param {object|string} guide - a guide or its id
 * @param {(code: string) => boolean} hasPermission
 * @param {(slug: string) => boolean} hasFeature
 * @param {boolean} isPhone
 */
export function canRunGuide(guide, { hasPermission, hasFeature, isPhone = false } = {}) {
  const g = typeof guide === 'string' ? getGuide(guide) : guide;
  if (!g) return false;
  if (g.desktopOnly && isPhone) return false;
  if (!meetsPermission(g.permission, hasPermission)) return false;
  if (g.featureSlug && hasFeature && !hasFeature(g.featureSlug)) return false;
  return true;
}

/**
 * Guides this user can actually run.
 *
 * The backend already filters the journey it hands down, so this is for the
 * launcher and for Dex offers, where the candidate list starts local.
 */
export function accessibleGuides(opts = {}) {
  return GUIDE_LIST.filter((g) => canRunGuide(g, opts));
}

/** Lexical match over title, summary and keywords. Ranking, not retrieval. */
export function searchGuides(query, guides = GUIDE_LIST) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return [];
  const terms = q.split(/\s+/).filter(Boolean);

  return guides
    .map((g) => {
      const haystack = [g.title, g.summary, ...(g.keywords || [])].join(' ').toLowerCase();
      const score = terms.reduce((acc, t) => {
        if (g.title.toLowerCase().includes(t)) return acc + 3;
        if ((g.keywords || []).some((k) => k.toLowerCase().includes(t))) return acc + 2;
        if (haystack.includes(t)) return acc + 1;
        return acc;
      }, 0);
      return { guide: g, score };
    })
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((r) => r.guide);
}

export { ORIENTATION_QUICK } from './orientation';
